import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import type { Shop, ShopOutcome } from '../services/apiService';
import { metaStyle as meta } from '../utils/shopHealth';
import { needsAttention } from '../utils/shopPresentation';
import { findByRef, isReservedRef } from '../utils/shopRef';
import ShopRoom from '../components/ShopRoom';

// The route behind `/feeds/:ref`.
//
// IT READS FOR ITSELF, AND THAT IS DELIBERATE. A room opened from the Dashboard's
// health banner has no hub state in memory, so "load it once on the hub and pass
// it down" is not available — the spec says each route reads on mount, and says
// it in those words rather than claiming a shared read that does not exist.
// The cost is real: hub → room → hub is three `getShops`. It is named in the
// change's Risks rather than hidden, and a shared cache is its own change with
// its own staleness semantics, not something to smuggle in as part of a move.

const ShopRoomPage: React.FC = () => {
  const { ref = '' } = useParams();
  const navigate = useNavigate();
  const [shops, setShops] = useState<Shop[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [outcome, setOutcome] = useState<ShopOutcome | null>(null);
  const [staleFeedIds, setStaleFeedIds] = useState<ReadonlySet<number>>(new Set());

  const load = useCallback(
    () => apiService.getShops()
      .then(o => { setShops(o.shops); setFailed(false); })
      .catch(() => setFailed(true)),
    []
  );

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    apiService.getShopOutcome().then(setOutcome).catch(() => setOutcome(null));
    apiService.getSetupStatus()
      .then(s => setStaleFeedIds(new Set(((s as any).staleUploads ?? []).map((f: any) => f.id))))
      .catch(() => setStaleFeedIds(new Set()));
  }, []);

  const backToHub = (notice: string) => navigate('/feeds', { state: { notice } });

  // A reserved segment is a screen, not a shop. React Router already ranks the
  // static routes above this one, so this only matters if one is ever removed —
  // at which point a shop named `upload` would silently take its URL.
  if (isReservedRef(ref)) return null;

  if (failed) {
    return (
      <section className="stats-section" data-testid="shop-room-error">
        <p role="alert" style={{ color: 'var(--danger)' }}>
          We could not load this shop just now. Reload the page to try again — nothing has changed.
        </p>
        <p><Link to="/feeds">Back to your shops</Link></p>
      </section>
    );
  }

  if (shops === null) {
    return <section className="stats-section"><p style={meta}>Loading…</p></section>;
  }

  const shop = findByRef(shops, ref);

  // GONE, NOT BROKEN. A shop can leave the offer between a bookmark being made
  // and being followed — removed in another tab, a programme withdrawn by the
  // network. That is an ordinary outcome and reads as one.
  if (!shop) {
    return (
      <section className="stats-section" data-testid="shop-room-missing">
        <h2>That shop is no longer in your list</h2>
        <p style={meta}>
          It may have been removed, or your network may no longer be offering it.
        </p>
        <p><Link to="/feeds">Back to your shops</Link></p>
      </section>
    );
  }

  return (
    <>
      <p style={{ ...meta, marginBottom: '0.75rem' }}>
        <Link to="/feeds" data-testid="back-to-feeds">← Your shops</Link>
      </p>
      <ShopRoom
        shop={shop}
        outcome={outcome?.rows.find(r => r.feedId === shop.feedId) ?? null}
        outcomeWindowDays={outcome?.windowDays ?? null}
        needsAttention={needsAttention(shop, staleFeedIds)}
        onChanged={load}
        onGone={backToHub}
      />
    </>
  );
};

export default ShopRoomPage;
